// @ts-nocheck
import { X } from "lucide-react";
import { Dropdown } from "../../components/ui/dropdown";
import { Button } from "../../components/ui/button";

export const RETRY_AGENT_ALL = '__all_retry_agents__';
export const RETRY_VERSION_ALL = '__all_retry_versions__';
export const RETRY_OUTCOME_FIELD_ALL = '__all_retry_outcome_fields__';
export const RETRY_OUTCOME_VALUE_ANY = '__any_retry_outcome_values__';

export interface RetryScopeOptions {
  agentNames: string[];
  versionNames: string[];
  versionsByAgent: Record<string, string[]>;
}

export interface RetryOutcomeOptions {
  fields: string[];
  values: Record<string, Array<{ normalized: string; label: string }>>;
}

interface CallsRetryBarProps {
  retryAgentFilter: string;
  setRetryAgentFilter: (v: string) => void;
  retryVersionFilter: string;
  setRetryVersionFilter: (v: string) => void;
  retryOutcomeField: string;
  setRetryOutcomeField: (v: string) => void;
  retryOutcomeValue: string;
  setRetryOutcomeValue: (v: string) => void;
  retryScopeOptions: RetryScopeOptions;
  retryOutcomeOptions: RetryOutcomeOptions;
  matchCount: number;
  totalCount: number;
  onClear: () => void;
}

export function CallsRetryBar({
  retryAgentFilter,
  setRetryAgentFilter,
  retryVersionFilter,
  setRetryVersionFilter,
  retryOutcomeField,
  setRetryOutcomeField,
  retryOutcomeValue,
  setRetryOutcomeValue,
  retryScopeOptions,
  retryOutcomeOptions,
  matchCount,
  totalCount,
  onClear,
}: CallsRetryBarProps) {
  const hasActiveFilters = 
    retryAgentFilter !== RETRY_AGENT_ALL || 
    retryVersionFilter !== RETRY_VERSION_ALL || 
    retryOutcomeField !== RETRY_OUTCOME_FIELD_ALL || 
    retryOutcomeValue !== RETRY_OUTCOME_VALUE_ANY;

  return (
    <div className="rounded-xl border border-line bg-white p-5">
      <div className="grid grid-cols-[1fr_1fr_auto] gap-x-6 gap-y-5 items-center">
        {/* Row 1 */}
        <div className="flex items-center gap-3">
          <span className="text-xs font-medium text-ink-2 w-16 text-right">Agent</span>
          <div className="flex-1">
            <Dropdown
              ariaLabel="Select Agent"
              value={retryAgentFilter}
              className="h-9 w-full text-sm font-medium border-transparent shadow-none focus:ring-0 px-2 bg-transparent hover:bg-soft transition-colors rounded-md"
              options={[
                { value: RETRY_AGENT_ALL, label: "All agents" },
                ...retryScopeOptions.agentNames.map(name => ({ value: name, label: name }))
              ]}
              onChange={(value) => {
                setRetryAgentFilter(value);
                setRetryVersionFilter(RETRY_VERSION_ALL);
                setRetryOutcomeField(RETRY_OUTCOME_FIELD_ALL);
                setRetryOutcomeValue(RETRY_OUTCOME_VALUE_ANY);
              }}
            />
          </div>
        </div>

        <div className="flex items-center gap-3">
          <span className="text-xs font-medium text-ink-2 w-16 text-right">Version</span>
          <div className="flex-1">
            <Dropdown
              ariaLabel="Select Version"
              value={retryVersionFilter}
              className={`h-9 w-full text-sm font-medium border-transparent shadow-none focus:ring-0 px-2 bg-transparent hover:bg-soft transition-colors rounded-md ${retryAgentFilter === RETRY_AGENT_ALL ? 'opacity-50 pointer-events-none' : ''}`}
              options={[
                { value: RETRY_VERSION_ALL, label: "All versions" },
                ...(retryScopeOptions.versionsByAgent[retryAgentFilter] || []).map(ver => ({ value: ver, label: ver }))
              ]}
              onChange={(value) => {
                setRetryVersionFilter(value);
                setRetryOutcomeField(RETRY_OUTCOME_FIELD_ALL);
                setRetryOutcomeValue(RETRY_OUTCOME_VALUE_ANY);
              }}
            />
          </div>
        </div>

        {/* Empty cell for top right */}
        <div></div>

        {/* Row 2 */}
        <div className="flex items-center gap-3">
          <span className="text-xs font-medium text-ink-2 w-16 text-right leading-tight">Outcome<br/>Field</span>
          <div className="flex-1">
            <Dropdown
              ariaLabel="Select Outcome Field"
              value={retryOutcomeField}
              className={`h-9 w-full text-sm font-medium border-transparent shadow-none focus:ring-0 px-2 bg-transparent hover:bg-soft transition-colors rounded-md ${retryVersionFilter === RETRY_VERSION_ALL || retryOutcomeOptions.fields.length === 0 ? 'opacity-50 pointer-events-none' : ''}`}
              options={[
                { value: RETRY_OUTCOME_FIELD_ALL, label: "All outcome fields" },
                ...retryOutcomeOptions.fields.map(field => ({ value: field, label: field }))
              ]}
              onChange={(value) => {
                setRetryOutcomeField(value);
                setRetryOutcomeValue(RETRY_OUTCOME_VALUE_ANY);
              }}
            />
          </div>
        </div>

        <div className="flex items-center gap-3">
          <span className="text-xs font-medium text-ink-2 w-16 text-right leading-tight">Outcome<br/>Value</span>
          <div className="flex-1">
            <Dropdown
              ariaLabel="Select Outcome Value"
              value={retryOutcomeValue}
              className={`h-9 w-full text-sm font-medium border-transparent shadow-none focus:ring-0 px-2 bg-transparent hover:bg-soft transition-colors rounded-md ${retryOutcomeField === RETRY_OUTCOME_FIELD_ALL || retryVersionFilter === RETRY_VERSION_ALL ? 'opacity-50 pointer-events-none' : ''}`}
              options={[
                { value: RETRY_OUTCOME_VALUE_ANY, label: "Any value" },
                ...(retryOutcomeOptions.values[retryOutcomeField] || []).map(option => ({ value: option.normalized, label: option.label }))
              ]}
              onChange={setRetryOutcomeValue}
            />
          </div>
        </div>

        {/* Match Count Bottom Right */}
        <div className="text-xs font-medium text-ink-2 text-right leading-tight pl-2">
          {matchCount} of {totalCount}<br/>retryable<br/>entries match
        </div>
      </div>
    </div>
  );
}
